import ModalHeader from '../../../../../../components/Modal/ModalHeader';
import { IonButton, IonCol, IonGrid, IonIcon, IonLabel, IonModal, IonRow, IonSpinner, IonText, IonToast } from '@ionic/react';
import { checkmarkCircle, documentTextOutline, mailOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { enviarCupomTotemEmail, requestService } from '../../../../../../Utils/Services';
import TecladoVirtual from './TecladoVirtual';

interface Props {
    pedidoPk: string;
    numeroPedido: string;
    dadosEmpresa: any;
    produtos: any[];
    valorTotal: string;
    tipoRetirada: string;
    formaPagamento: any[];
    clienteNome: string;
    onFinalizar: () => void;
}

const CONFIRMACAO_TIMEOUT = 20; // segundos

function formatarValorMonetario(valor: any) {
    if (valor === null || valor === undefined || valor === '') {
        return '0,00';
    }

    const numero = typeof valor === 'number'
        ? valor
        : parseFloat(valor.toString().replace(/\./g, '').replace(',', '.'));

    if (Number.isNaN(numero)) {
        return '0,00';
    }

    return numero.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

const TelaConfirmacao: React.FC<Props> = ({
    pedidoPk, numeroPedido, dadosEmpresa, produtos, valorTotal,
    tipoRetirada, formaPagamento, clienteNome, onFinalizar
}) => {
    const history = useHistory();
    const [showCupom, setShowCupom] = useState(false);
    const [showEmailInput, setShowEmailInput] = useState(false);
    const [email, setEmail] = useState('');
    const [enviandoEmail, setEnviandoEmail] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [textToast, setTextToast] = useState('');

    // Timer regressivo com reset em interação
    const [countdown, setCountdown] = useState(CONFIRMACAO_TIMEOUT);
    const countdownRef = useRef(CONFIRMACAO_TIMEOUT);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const modalAbertaRef = useRef(false);

    const resetCountdown = useCallback(() => {
        countdownRef.current = Math.min(countdownRef.current + 20, CONFIRMACAO_TIMEOUT);
        setCountdown(countdownRef.current);
    }, []);

    // Mantém ref sincronizado com estado das modais
    useEffect(() => {
        modalAbertaRef.current = showCupom || showEmailInput;
    }, [showCupom, showEmailInput]);

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            // Pausa o timer enquanto qualquer modal estiver aberta
            if (modalAbertaRef.current) return;
            countdownRef.current -= 1;
            setCountdown(countdownRef.current);
            if (countdownRef.current <= 0) {
                if (intervalRef.current) clearInterval(intervalRef.current);
                onFinalizar();
            }
        }, 1000);

        const events = ['touchstart', 'mousedown', 'keydown'];
        events.forEach(ev => window.addEventListener(ev, resetCountdown, { passive: true }));

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            events.forEach(ev => window.removeEventListener(ev, resetCountdown));
        };
    }, [onFinalizar, resetCountdown]);

    const formaPagamentoTexto = formaPagamento
        ?.map((f: any) => {
            const nome = f?.pagamento || f?.formaPagamentoNome || f?.tipo;
            const valor = f?.valor ?? f?.formaPagamentoValor;

            if (!nome) {
                return '';
            }

            return valor !== undefined && valor !== null && valor !== ''
                ? `${nome}: R$ ${formatarValorMonetario(valor)}`
                : nome;
        })
        .filter(Boolean)
        .join(', ') || '';

    function formataTelefone(numero: any) {
        if (numero?.length === 13) {
            const m = numero.replace('55', '').match(/(\d{2})(\d{5})(\d{4})/);
            return m ? `(${m[1]}) ${m[2]}-${m[3]}` : '';
        } else if (numero?.length === 12) {
            const m = numero.replace('55', '').match(/(\d{2})(\d{4})(\d{4})/);
            return m ? `(${m[1]}) ${m[2]}-${m[3]}` : '';
        }
        return '';
    }

    function handleEnviarEmail() {
        if (!email || !email.includes('@')) {
            setTextToast('Informe um email válido.');
            setShowToast(true);
            return;
        }

        setEnviandoEmail(true);
        resetCountdown();

        requestService(
            enviarCupomTotemEmail.url.replace('{id}', pedidoPk.replace(/#/g, '%23')),
            {
                method: enviarCupomTotemEmail.method,
                headers: [['token', window.localStorage.getItem('token')]],
                body: JSON.stringify({ email }),
                credentials: 'include',
            },
            history,
            () => {
                setEnviandoEmail(false);
                setShowEmailInput(false);
                setEmail('');
                setTextToast('Cupom enviado para ' + email);
                setShowToast(true);
            },
            (error: any) => {
                setEnviandoEmail(false);
                setTextToast(error?.message || 'Erro ao enviar email.');
                setShowToast(true);
            }
        );
    }

    function renderCupom() {
        return (
            <div className="totem-confirmacao-cupom">
                <IonGrid>
                    <IonRow>
                        <IonCol>
                            <IonText>
                                <h2 style={{ margin: '10px 0 5px 0' }}><b>{dadosEmpresa?.empresaNome || ''}</b></h2>
                                {dadosEmpresa?.empresaEndereco && <p style={{ margin: '2px 0' }}>Endereço: {dadosEmpresa.empresaEndereco}</p>}
                                {dadosEmpresa?.empresaTelefone && <p style={{ margin: '2px 0' }}>Tel: {formataTelefone(dadosEmpresa.empresaTelefone)}</p>}
                                <p style={{ margin: '2px 0' }}>Data: {new Date().toLocaleDateString('pt-BR')} Hora: {new Date().toLocaleTimeString('pt-BR')}</p>
                            </IonText>
                        </IonCol>
                    </IonRow>
                </IonGrid>

                <IonGrid>
                    <hr style={{ borderTop: '1px solid', borderStyle: 'dashed', borderColor: 'grey', margin: '5px' }} />
                    <IonRow>
                        <IonCol>
                            <h2 style={{ padding: 0, margin: 0 }}>Pedido: {numeroPedido}</h2>
                        </IonCol>
                        <IonCol>
                            <IonText style={{ width: '100%', textAlign: 'right' }}>
                                <h2 style={{ padding: 0, margin: 0 }}>{clienteNome}</h2>
                            </IonText>
                        </IonCol>
                    </IonRow>
                    <hr style={{ borderTop: '1px solid', borderStyle: 'dashed', borderColor: 'grey', margin: '5px' }} />

                    <IonRow>
                        <IonCol>
                            <IonText><h2 style={{ margin: '5px 0 5px 0' }}>CUPOM NÃO FISCAL</h2></IonText>
                        </IonCol>
                    </IonRow>
                    {produtos && produtos.length > 0 && (
                        <>
                            <IonRow>
                                <IonCol style={{ padding: '0 0 0 5px' }} size='1'>#</IonCol>
                                <IonCol style={{ padding: '0 0 0 5px' }} size='7'>DESCRIÇÃO</IonCol>
                                <IonCol style={{ padding: '0 0 0 5px' }}>QTDE</IonCol>
                                <IonCol style={{ padding: '0 0 0 5px', textAlign: 'right', marginRight: '5px' }}>VALOR</IonCol>
                            </IonRow>
                            {produtos.map((item: any, index: any) => (
                                <IonRow>
                                    <IonCol style={{ padding: '0 0 0 5px' }} size='1'>{String(index + 1).padStart(2, '0')}</IonCol>
                                    <IonCol style={{ padding: '0 0 0 5px' }} size='7'>



                                        {item.descricao && (Array.isArray(item.descricao)) ? (
                                            <>
                                                <>
                                                    {(item.tipo === "pizzas") ? (
                                                        <div>{item.tamanho} {item.quantidadeSabores} SABORES (<IonLabel style={{ textAlign: "right", fontSize: "12px" }}>R$ {item.valor?.toLocaleString('pt-br', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}</IonLabel>)</div>
                                                    ) : (
                                                        <div>{item.categoria} (<IonLabel style={{ textAlign: "right", fontSize: "12px" }}>R$ {item.valor?.toLocaleString('pt-br', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}</IonLabel>)</div>
                                                    )}
                                                </>
                                                {item.descricao.map((item: any, index: number) => (
                                                    <div key={"desc" + index}>
                                                        {item.categoria === "Observação" ? (
                                                            <div style={{ textDecoration: "underline" }}>
                                                                Observação: {item.descricao}
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                - {item.descricao}{" "}
                                                                <IonLabel style={{ textAlign: "right", fontSize: "12px" }}>
                                                                    R$ {item.valor?.toLocaleString("pt-br", {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })}
                                                                </IonLabel>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </>

                                        ) : (
                                            <>
                                                {item.descricao} (<IonLabel style={{ textAlign: "right", fontSize: "12px" }}>R$ {item.valor?.toLocaleString('pt-br', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}</IonLabel>)
                                            </>
                                        )}



                                    </IonCol>
                                    <IonCol style={{ padding: '0 0 0 5px' }}>{item.quantidade.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}</IonCol>
                                    <IonCol style={{ padding: '0 0 0 5px', textAlign: 'right', marginRight: '5px' }}>{item.subtotal.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}</IonCol>
                                </IonRow>
                            ))}
                            <hr style={{ borderTop: '1px solid', borderStyle: 'dashed', borderColor: 'grey', margin: '10px 5px 5px 5px' }}></hr>
                        </>
                    )}

                    {formaPagamentoTexto && (
                        <IonRow>
                            <IonCol size="6"><IonText><strong>FORMA PAGAMENTO:</strong></IonText></IonCol>
                            <IonCol size="6" style={{ textAlign: 'right' }}><IonText>{formaPagamentoTexto.toUpperCase()}</IonText></IonCol>
                        </IonRow>
                    )}

                    {/* {tipoRetirada && (
                        <IonRow>
                            <IonCol size="6"><IonText><strong>RETIRADA:</strong></IonText></IonCol>
                            <IonCol size="6" style={{ textAlign: 'right' }}><IonText>{tipoRetirada.toUpperCase()}</IonText></IonCol>
                        </IonRow>
                    )} */}

                    <IonRow>
                        <IonCol size="12" style={{ textAlign: 'right' }}>
                            <h2 style={{ margin: 0 }}>TOTAL R$ {valorTotal}</h2>
                        </IonCol>
                    </IonRow>
                </IonGrid>
            </div>
        );
    }

    const TOTAL_SECONDS = 10;
    const showAlerta = countdown <= TOTAL_SECONDS;

    return (
        <div className="totem-tela-confirmacao">
            <div className="totem-tela-body totem-center-content">
                <IonIcon icon={checkmarkCircle} className="totem-confirmacao-icone" color="success" />
                <h1>Pedido Confirmado!</h1>
                <h2 style={{ marginBottom: '4px' }}>Pedido: <strong>{numeroPedido}</strong></h2>
                <p style={{ fontSize: '1rem', color: '#888', marginTop: '4px', marginBottom: '24px' }}>
                    Dirija-se ao balcão e aguarde chamar o seu <strong>nome/número.</strong>
                </p>


                {showAlerta && (
                    <div className="totem-confirmacao-countdown-overlay">
                        <div className="totem-confirmacao-countdown-card">
                            {/* <IonIcon icon={timeOutline} color="danger" className="totem-countdown-icon" /> */}
                            <span className={`totem-countdown-number${countdown <= 10 ? ' totem-countdown-alerta' : ''}`}>
                                {countdown}
                            </span>
                            <span className="totem-countdown-label"></span>
                            <div className="totem-countdown-bar-track">
                                <div
                                    className={`totem-countdown-bar-fill${countdown <= 10 ? ' totem-countdown-alerta' : ''}`}
                                    style={{ width: `${(countdown / TOTAL_SECONDS) * 100}%` }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className="totem-confirmacao-acoes">
                    <IonButton className="totem-btn" fill="outline" color="medium" onClick={() => { setShowCupom(true); resetCountdown(); }}>
                        <IonIcon icon={documentTextOutline} slot="start" />
                        Ver cupom
                    </IonButton>
                    <IonButton className="totem-btn" fill="outline" color="medium" onClick={() => { setShowEmailInput(true); resetCountdown(); }}>
                        <IonIcon icon={mailOutline} slot="start" />
                        Enviar por email
                    </IonButton>
                </div>

                <div style={{ marginTop: '32px' }}>
                    <IonButton className="totem-btn" color="totem" onClick={onFinalizar}>
                        Finalizar
                    </IonButton>
                </div>
            </div>

            {/* Modal cupom */}
            <IonModal isOpen={showCupom} onDidDismiss={() => setShowCupom(false)}>
                <ModalHeader title="Cupom do pedido" onDismiss={() => setShowCupom(false)} />
                <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#fff', padding: '16px' }}>
                    {renderCupom()}
                </div>
            </IonModal>

            {/* Modal enviar email com teclado virtual */}
            <IonModal className="fullscreen-modal totem-email-dialog totem-themed-modal" isOpen={showEmailInput} onDidDismiss={() => { setShowEmailInput(false); setEmail(''); }}>
                <ModalHeader title="Enviar cupom por email" onDismiss={() => { setShowEmailInput(false); setEmail(''); }} />
                <div className="totem-email-modal xeguei-custom-modal-body">
                    <IonIcon icon={mailOutline} style={{ fontSize: '3rem', color: 'var(--ion-color-totem)', marginBottom: '8px' }} />

                    <p style={{ color: '#888', textAlign: 'center', margin: '0 0 12px 0', fontSize: '0.9rem' }}>
                        Pedido: <strong>{numeroPedido}</strong>
                    </p>

                    <div className="totem-input-display" style={{ margin: '0 0 8px 0', minHeight: '48px', width: '100%', maxWidth: 'calc(10 * 60px + 9 * 4px)', marginLeft: 'auto', marginRight: 'auto' }}>
                        <IonText color="dark">
                            <h2 style={{ margin: 0, fontSize: '1.2rem', wordBreak: 'break-all' }}>{email || 'seu@email.com'}</h2>
                        </IonText>
                    </div>

                    <TecladoVirtual
                        value={email}
                        onChange={(v: string) => { setEmail(v); resetCountdown(); }}
                        maxLength={60}
                        emailMode
                    />

                    <div className="totem-email-actions">
                        <IonButton className="totem-btn" fill="outline" color="danger" onClick={() => { setShowEmailInput(false); setEmail(''); }}>
                            Cancelar
                        </IonButton>
                        <IonButton className="totem-btn" color="success" onClick={handleEnviarEmail} disabled={enviandoEmail}>
                            {enviandoEmail ? <IonSpinner name="crescent" /> : 'Enviar'}
                        </IonButton>
                    </div>
                </div>
            </IonModal>

            <IonToast isOpen={showToast} position="middle" onDidDismiss={() => setShowToast(false)} message={textToast} duration={3000} />
        </div>
    );
};

export default TelaConfirmacao;
